<template>
  <b-container v-if="this.$root.signedIn" class="pt-3">
    <b-row>
      <b-col cols=12>
        <b-card no-body class="mb-1" style="width:100%">
          <b-card-header header-tag="header" class="p-1" role="tab">
            <b-btn block href="#" v-b-toggle.accordion1 variant="primary">
              Search Criteria
              <font-awesome-icon icon="caret-down" size="lg" v-if="!showSearch"/> 
              <font-awesome-icon icon="caret-up" size="lg" v-if="showSearch"/>
            </b-btn>
          </b-card-header>
          <b-collapse id="accordion1" visible accordion="accordion1" role="tabpanel" v-model="showSearch">
            <b-row v-if="!printing" class="pt-2 pl-1">
              <b-col>
                <scoring-event-search 
                  :skip = "{
                    programme: true
                  }" 
                  :multiple = "{
                    region: true,
                    industry: true,
                    year: true
                  }"
                  @update:customer = "customer = $event"
                  @update:industry = "industries = $event" 
                  @update:year = "years = $event"
                  @update:country = "country = $event"
                  @update:region = "regions = $event"
                  @update:gear = "gear = $event"
                />
              </b-col>
              <b-col>
                <b-container>
                  <b-card>
                    <h4>More Fields</h4>
                    <b-row>
                      <survey-run-selection :no-new="true" :customer="customerId" :required="false" @update:surveyRun="surveyRun = $event"/>
                    </b-row>
                    <b-row>
                      <g-p-c-session-selection :customer="customerId" v-model="gpcSession" :required="false"/>
                    </b-row>
                    <b-row class="text-left pt-2">
                      Status
                    </b-row>
                    <b-row>
                      <b-form-select v-model="sessionStatus" class="mb-3" :options="['', 'Draft', 'Sent', 'Completed']"/>
                    </b-row>
                    <b-row class="text-left pt-2">
                      <label for="date">Date first entered</label> 
                    </b-row>
                    <b-row>
                      <datepicker v-model="from_date" id="date" :disabled-dates="{'from': new Date()}" :clear-button="true"/> 
                      <div class="pl-2 pr-2">to</div>
                      <datepicker v-model="to_date" id="date" :disabled-dates="{'from': new Date()}" :clear-button="true"/>
                    </b-row>
                    <b-row class="text-left pt-2">
                      Owner
                    </b-row>
                    <b-row>
                      <b-form-input v-model="partial_name" class="mb-3" placeholder="Name...." id="person"/>
                    </b-row>
                    <b-row class="text-left pt-2">
                      <label>Keywords</label> 
                    </b-row>
                    <b-row>
                      <b-col class="text-left">
                        <vue-tags-input v-model="keyword" :tags="keywords" @tags-changed="newTags => keywords = newTags" placeholder="Add keywords..." />
                      </b-col>
                    </b-row>
                  </b-card>
                </b-container>
              </b-col>
            </b-row>
            <b-row class="pt-3 pb-3">
              <b-col class="text-center">
                <b-button variant="success" @click="search()" v-if="!searching">Search</b-button>
              </b-col>
            </b-row>
          </b-collapse>
        </b-card>
        <b-tabs v-model="tabIndex">
          <b-tab title="Results" v-if="!printing">
            <div v-if="!searching">
              <table class="table table-striped" v-if="results">
                <thead>
                  <tr>
                    <th class="text-left">Customer</th>
                    <th class="text-left">Goal Setting</th>
                    <th class="text-left">Scoring Event</th>
                    <th class="text-left">GPC Session</th>
                    <th class="text-left">Problem/Action(s)</th>
                    <th/>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in results" :key="item.id" class='c'>
                    <td class="text-nowrap text-left align-top">
                      <b>{{ item.surveyRun.customer.name }}</b>
                      <br>
                      {{ item.surveyRun.customer.country }}
                      <br v-if="item.surveyRun.customer.region">
                      {{ item.surveyRun.customer.region }}
                      <br>
                      {{ item.surveyRun.customer.industry }} 
                      <br v-if="item.surveyRun.customer.secondary_industry">
                      {{ item.surveyRun.customer.secondary_industry }} 
                    </td>
                    <td class="text-nowrap text-left align-top">
                      <b>{{ item.date_created | moment('Do MMM YYYY') }}</b>
                      <br>
                      Status: {{ item.status }} 
                    </td>
                    <td class="text-nowrap text-left align-top">
                      <b>{{ item.surveyRun.programme.name }} Programme</b>
                      <br>
                      Gear: {{ item.gear }}
                      <br>
                      {{ item.surveyRun.period }} {{ item.surveyRun.year }}
                    </td>
                    <td class="text-nowrap text-left align-top">
                      <div v-if="item.gpc">
                        <b>{{ item.gpc.session_date | moment('Do MMM YYYY') }}</b>
                        <br>
                        Coach: {{ item.gpc.coach_name }} 
                        <br>
                        Owner: {{ item.gpc.responsible_name }}
                      </div>
                    </td>
                    <td class="text-left align-top">
                      <b>Problem:</b> <span v-html="item.problem"/>
                      <br>
                      <b>Action(s):</b> <span v-html="item.actions"/>
                    </td>
                    <td class="align-top">
                      <b-button-group vertical>
                        <b-button variant="secondary" @click="session = item; tabIndex = 1;">View</b-button>
                        <b-button variant="primary" @click="$emit('edit-session', item);">Edit</b-button>
                        <vue-confirmation-button ref="deleteButton" :css="'btn btn-danger sm'" :messages="['Delete','Are you sure?','Deleted, reloading.....']" @confirmation-success="deleteItem(item);"/>
                      </b-button-group>
                    </td>
                  </tr>
                  <tr class='c' v-if="!total">
                    <td colspan=100>No results</td>
                  </tr>
                </tbody>
              </table>
              <b-pagination size="sm" :total-rows="total" v-model="page" :per-page="pageSize" v-if="total > 0"/>
            </div>
          </b-tab>
          <b-tab title="SMART Goal Session" v-if="session">
            <div id="goal-session-view">
              <div class="page">
                <b-row class="pb-3 pt-2 align-middle my-auto">
                  <b-col class="align-middle my-auto">
                    <b-img left src="/static/traction.png" height="40px"/>
                  </b-col>
                  <b-col>
                    <b-img right src="/static/partial_logo.png" height="70px"/>
                  </b-col>
                </b-row>
                <b-row class="pb-2 text-center">
                  <b-col class="text-left">
                    <h2 class="gpc-title text-center">{{ session.gpc ? 'GPC - Follow Up' : 'SMART Goal Session' }}</h2>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col class="text-left pb-3">
                    <b>{{ session.gpc ? 'GPC SESSION ': '' }}DATE</b> - {{ (session.gpc ? session.gpc.session_date : session.date_created) | moment('Do MMMM YYYY') }}
                  </b-col>
                </b-row>
                <b-row>
                  <b-col class="text-left pb-3">
                    <b>PROBLEM OWNER</b> - {{ session.problem_owner }}
                  </b-col>
                </b-row>
                <b-row>
                  <b-col class="text-left pb-3">
                    <b>PROBLEM</b> - {{ session.problem }}
                  </b-col>
                </b-row>
                <b-row v-if="session.gpc">
                  <b-col class="text-left pb-3">
                    <b>GROUP MEMBERS</b>
                    <br>
                    <span v-html="session.gpc.participants"/>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col class="text-left pb-3">
                    <b>{{ session.gpc ? 'GPC ':'' }}ACTIONS</b>
                    <br>
                    <span v-html="session.actions"/>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col class="text-left pb-3">
                    <b>{{ session.gpc ? 'GPC FOLLOW UP':'RECOMMENDED STEPS' }}</b> -  It is recommended the three steps below are followed
                  </b-col>
                </b-row>
                <b-row>
                  <b-col class="text-left pb-3">
                    <table class="table">
                      <thead class="thead-light">
                        <tr>
                          <th scope="col" class="border-left border-right"></th>
                          <th scope="col" class="border-left border-right">ACTIVITY</th>
                          <th scope="col" class="border-left border-right">RESOURCE</th>
                        </tr>
                      </thead>
                      <tr>
                        <td class="border-left border-right text-nowrap">STEP ONE</td>
                        <td class="border-left border-right">For each of the actions {{ session.gpc ? 'from your GPC session' : 'identified' }} set S.M.A.R.T goals.</td>
                        <td class="border-left border-right">SMART GOAL PLANNER - TEMPLATE</td>
                      </tr>
                      <tr>
                        <td class="border-left border-right text-nowrap">STEP TWO</td>
                        <td class="border-left border-right">Delegate goals/tasks to staff. If your company has a performance review/ coaching procedure then it is advisable that this delegation is aligned to the staff members ongoing development.</td>
                        <td class="border-left border-right">DELEGATION GUIDE</td>
                      </tr>
                      <tr>
                        <td class="border-left border-right border-bottom text-nowrap">STEP THREE</td>
                        <td class="border-left border-right border-bottom">Support staff with regular feedback.</td>
                        <td class="border-left border-right border-bottom">FEEDBACK GUIDE</td>
                      </tr>
                    </table>
                  </b-col>
                </b-row>
                <b-row class="pt-3 footer">
                  <b-col class="text-right align-bottom">
                    <b-img right fluid class="img-responsive" src="/static/footer.png"/>
                    <div class="sf-copy carousel-caption text-right pb-0">
                      &copy; IAG New Zealand Limited T/A NZI {{ new Date() | moment('YYYY') }}
                    </div>
                  </b-col>
                </b-row>
              </div>
              <div v-for="smartGoal in smartGoals" :key="smartGoal.id" class="page" v-if="smartGoals && smartGoals.length > 0">
                <b-row class="pb-3 pt-2 align-middle my-auto">
                  <b-col class="align-middle my-auto">
                    <b-img left src="/static/traction.png" height="40px"/>
                  </b-col>
                  <b-col>
                    <b-img right src="/static/partial_logo.png" height="70px"/>
                  </b-col>
                </b-row>
                <b-row class="pb-2 text-center">
                  <b-col class="text-left">
                    <h2 class="gpc-title text-center">S.M.A.R.T GOAL Planner</h2>
                  </b-col>
                </b-row>
                <b-row class="pb-2 text-center border-left border-right border-top border-bottom">
                  <b-col class="text-left">
                    <span class="gpc-title text-center goal-header">S</span><b>PECIFIC - </b> {{ session.gpc ? 'GPC' : '' }} ACTION
                    <br>
                    <span v-html="smartGoal.s"/>
                  </b-col>
                </b-row>
                <b-row class="pb-2 text-center border-left border-right border-top border-bottom">
                  <b-col class="text-left">
                    <span class="gpc-title text-center goal-header">M</span><b>EASURES - </b> HOW WILL YOU KNOW IT WAS SUCCESSFUL (Lag and Lead Metrics)
                    <br>
                    <span v-html="smartGoal.m"/>
                  </b-col>
                </b-row>
                <b-row class="pb-2 text-center border-left border-right border-top border-bottom">
                  <b-col class="text-left">
                    <span class="gpc-title text-center goal-header">A</span><b>CTION - </b> LIST THE MAIN TASKS
                    <br>
                    <span v-html="smartGoal.a"/>
                  </b-col>
                </b-row>
                <b-row class="pb-2 text-center border-left border-right border-top border-bottom">
                  <b-col class="text-left">
                    <span class="gpc-title text-center goal-header">R</span><b>ELEVANCE - </b> LIST BENEFITS FOR BUSINESS
                    <br>
                    <span v-html="smartGoal.r"/>
                  </b-col>
                </b-row>
                <b-row class="pb-2 text-center border-left border-right border-top border-bottom">
                  <b-col class="text-left">
                    <span class="gpc-title text-center goal-header">T</span><b>IME - </b> DECIDE TIMEFRAMES, WHEN THIS GOAL AND TASKS SHOULD BE FINISHED
                    <br>
                    <span v-html="smartGoal.t"/>
                  </b-col>
                </b-row>
                <b-row class="pt-3">
                  <b-col class="text-right align-bottom sf-copy pb-0">
                    &copy; IAG New Zealand Limited T/A NZI {{ new Date() | moment('YYYY') }}
                  </b-col>
                </b-row>
              </div>
              <div class="page" v-if="!smartGoals || smartGoals.length === 0">
                <b-row class="pb-3 pt-2 align-middle my-auto">
                  <b-col class="align-middle my-auto">
                    <b-img left src="/static/traction.png" height="40px"/>
                  </b-col>
                  <b-col>
                    <b-img right src="/static/partial_logo.png" height="70px"/>
                  </b-col>
                </b-row>
                <b-row class="pb-2 text-center">
                  <b-col class="text-left">
                    <h2 class="gpc-title text-center">S.M.A.R.T GOAL Planner</h2>
                  </b-col>
                </b-row>
                <b-row class="pb-2 text-center border-left border-right border-top border-bottom">
                  <b-col class="text-left">
                    <span class="gpc-title text-center goal-header">S</span><b>PECIFIC - </b> {{ session.gpc ? 'GPC' : '' }} ACTION {{ session.gpc ? '(Complete this S.M.A.R.T planner for each GPC Action)' : '' }}
                    <br>
                    <div class="blank-line" v-html="' '"/>
                  </b-col>
                </b-row>
                <b-row class="pb-2 text-center border-left border-right border-top border-bottom">
                  <b-col class="text-left">
                    <span class="gpc-title text-center goal-header">M</span><b>EASURES - </b> HOW WILL YOU KNOW IT WAS SUCCESSFUL (Lag and Lead Metrics)
                    <br>
                    <div class="blank-line" v-html="' '"/>
                  </b-col>
                </b-row>
                <b-row class="pb-2 text-center border-left border-right border-top border-bottom">
                  <b-col class="text-left">
                    <span class="gpc-title text-center goal-header">A</span><b>CTION - </b> LIST THE MAIN TASKS
                    <br>
                    <div class="blank-line" v-html="' '"/>
                  </b-col>
                </b-row>
                <b-row class="pb-2 text-center border-left border-right border-top border-bottom">
                  <b-col class="text-left">
                    <span class="gpc-title text-center goal-header">R</span><b>ELEVANCE - </b> LIST BENEFITS FOR BUSINESS
                    <br>
                    <div class="blank-line" v-html="' '"/>
                  </b-col>
                </b-row>
                <b-row class="pb-2 text-center border-left border-right border-top border-bottom">
                  <b-col class="text-left">
                    <span class="gpc-title text-center goal-header">T</span><b>IME - </b> DECIDE TIMEFRAMES, WHEN THIS GOAL AND TASKS SHOULD BE FINISHED
                    <br>
                    <div class="blank-line" v-html="' '"/>
                  </b-col>
                </b-row>
                <b-row class="pt-3">
                  <b-col class="text-right align-bottom sf-copy pb-0">
                    &copy; IAG New Zealand Limited T/A NZI {{ new Date() | moment('YYYY') }}
                  </b-col>
                </b-row>
              </div>
            </div>
          </b-tab>
        </b-tabs>
        <b-row class="pt-5 pb-5 text-right">
          <b-button @click="print();" v-if="session" class="align-self-right">Print</b-button>     
        </b-row>
      </b-col>
    </b-row>
  </b-container>
</template>
<script>
// @ is an alias to /src
import Datepicker from 'vuejs-datepicker';
import VueTagsInput from '@johmun/vue-tags-input';
import { gearDef } from '@/services/constants';
import ScoringEventSearch from '@/components/ScoringEventSearch.vue';
import SurveyRunSelection from '@/components/SurveyRunSelection.vue';
import GPCSessionSelection from '@/components/GPCSessionSelection.vue';
import * as API from '@/services/API';
import { Printd } from 'printd';
import vueConfirmationButton from 'vue-confirmation-button';
import ToggleButton from 'vue-js-toggle-button/src/Button';

export default {
  name: "SmartGoalSearch",
  components: {
    Datepicker,
    VueTagsInput,
    vueConfirmationButton,
    ScoringEventSearch,
    ToggleButton,
    SurveyRunSelection,
    GPCSessionSelection
  },
  watch: {
    page: {
      handler(newData) {
        if (newData) this.search();
      }
    },
    customer: {
      handler(newData) {
        this.customerId = (newData || {}).id;
      }
    },
    surveyRun: {
      handler(newData) {
        this.customer = (newData || {}).customer;
        this.customerId = ((newData || {}).customer || {}).id;
      }
    }
  },
  computed: {
    smartGoals: function() {
      if (!this.session || !this.session.smartGoals) return [];
      return this.session.smartGoals.filter(x => !x.is_deleted);
    }
  },
  data: function() {
    return {
      customer: null,
      customerId: null,
      industries: null,
      country: null,
      regions: null,
      gear: null, 
      years: [],
      keywords: [],
      keyword: '',
      keywordIsAnd: false,
      partial_name: null,
      searching: false,
      results: [],
      total: 0,
      page: 1,
      pageSize: 20,
      displayType: 'table',
      from_date: null,
      to_date: null,
      session: null,
      printing: false,
      showSearch: true,
      tabIndex: 0,
      sessionStatus: null,
      gpcSession: null,
      surveyRun: null
    };
  },
  methods: {
    print() {
      const printer = new Printd();
      printer.print(document.getElementById('goal-session-view'), '', function(win, doc, node, launchPrint) {
        // Get style elements
        const styles = [].slice.call(document.getElementsByTagName('style'));
        const links = [].slice.call(document.getElementsByTagName('link'));
        // append them to the the new document head element
        styles.forEach(styleElement => doc.head.appendChild(styleElement.cloneNode(true)));
        links.forEach(linkElement => doc.head.appendChild(linkElement.cloneNode(true)));
        const styleElement = doc.createElement('style');
        const innerStyle = doc.createTextNode('.footer { position: absolute; bottom: 0px; }');
        styleElement.appendChild(innerStyle);
        doc.head.appendChild(styleElement);

        setTimeout(() => launchPrint(win), 1000)
      });
    },
    deleteItem(item) {
      return API.deleteGoalSetting(item.surveyRun.id, item.id).then(results => {
        this.search();
      });
    },
    search() { 
      this.searching = true;
      this.showSearch = false;
      this.tabIndex = 0;
      this.session = null;
      const searchBody = {
        survey_run_id: (this.surveyRun || {}).id,
        gpc_id: (this.gpcSession || {}).id,
        status: this.sessionStatus,
        problem_owner: this.partial_name,
        customer_id: (this.customer || {}).id,
        country: this.country,
        years: this.years,
        regions: this.regions,   
        from_date: this.from_date,
        to_date: this.to_date,
        industries: this.industries,
        keywords: this.keywords.map(keyword => keyword.text),
        page: this.page,
        pageSize: this.pageSize,
        gear: this.gear
      };
      
      // clean out nulls
      Object.keys(searchBody).forEach(field => {
        if (!searchBody[field] || searchBody[field].length === 0) delete searchBody[field];
      });
      
      // do actual search
      API.searchGoalSettings(searchBody).then(results => {
        this.total = results.body.total;
        this.searching = false;
        this.results = results.body.results.map(row => {
          row.gear = Object.keys(gearDef).reduce((accumulator, percentage) => {
            if (row.surveyRun.overall_score >= percentage) return gearDef[percentage];
            return accumulator;
          }, 0);
          return row;
        });
      });
    }
  }
}
</script>
<style>
.buttonbar {
  padding-top: 4em;
  padding-bottom: 4em;
}
.gpc-title {
  font-family: Archer, serif;
  font-weight: bold;
  color: #EE3124;
  font-weight: bold;

}
.sf-copy {
  font-family: Archer, serif;
  color: #EE3124 !important;
  font-weight: bold;
  vertical-align: text-bottom
}
.page {
  page-break-after: always;
}
.goal-header {
  font-size: 1.5rem;
}
.verticaltext {
    position: relative; 
    padding-left:0px;
    padding-top: 20px;
    margin:1em 0;
    min-height:100px;
}
.blank-line {
  min-height: 150px !important;
  height: 150px !important;
}
.verticaltext_content1 {
    -webkit-transform: rotate(-90deg);
    -moz-transform: rotate(-90deg);
    -ms-transform: rotate(-90deg);
    -o-transform: rotate(-90deg);
    filter: progid:DXImageTransform.Microsoft.BasicImage(rotation=3);
    position: absolute;
    left: -55px;
    padding-right: 60px;
    white-space:nowrap;
}
.verticaltext_content2 {
    -webkit-transform: rotate(-90deg);
    -moz-transform: rotate(-90deg);
    -ms-transform: rotate(-90deg);
    -o-transform: rotate(-90deg);
    filter: progid:DXImageTransform.Microsoft.BasicImage(rotation=3);
    position: absolute;
    left: 0px;
}
.verticaltext_content3 {
    -webkit-transform: rotate(-90deg);
    -moz-transform: rotate(-90deg);
    -ms-transform: rotate(-90deg);
    -o-transform: rotate(-90deg);
    filter: progid:DXImageTransform.Microsoft.BasicImage(rotation=3);
    position: absolute;
    left: 0px;
}
</style>



// WEBPACK FOOTER //
// src/components/SmartGoalSearch.vue